import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Route, Dumbbell, Library, CalendarDays, ArrowRight, Sparkles } from "lucide-react";

export default function ClientDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [journey, setJourney] = useState(null);
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    api.get("/client/journey").then(({ data }) => setJourney(data));
    api.get("/content").then(({ data }) => setRecommended(data.filter((c) => c.recommended).slice(0, 3)));
  }, []);

  const na = journey?.next_appointment;

  return (
    <div>
      <div className="rounded-3xl bg-gradient-to-br from-primary to-indigo-700 p-8 text-white mb-6 relative overflow-hidden">
        <Sparkles className="absolute -right-4 -top-4 w-32 h-32 text-white/10" />
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Olá, {user?.name?.split(" ")[0]}!</h1>
        <p className="text-white/80 mt-1">Veja suas atividades, conteúdos e próximas consultas.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2" data-testid="journey-card">
          <div className="flex items-center gap-2 text-primary mb-3"><Route className="w-5 h-5" /><h3 className="font-heading font-medium">Minha Jornada</h3></div>
          {journey && (
            <>
              <div className="flex justify-between text-sm text-muted-foreground mb-1">
                <span>{journey.completed_exercises} de {journey.total_exercises} atividades concluídas</span>
                <span className="font-medium text-foreground">{journey.progress}%</span>
              </div>
              <Progress value={journey.progress} className="h-3" />
              <p className="text-sm text-muted-foreground mt-3">Continue realizando suas atividades até a próxima consulta.</p>
              <Button variant="outline" size="sm" className="rounded-full mt-4" onClick={() => nav("/app/jornada")} data-testid="view-journey-btn">Ver minha jornada <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
        </Card>

        <Card className="p-6" data-testid="next-appointment-card">
          <div className="flex items-center gap-2 text-primary mb-3"><CalendarDays className="w-5 h-5" /><h3 className="font-heading font-medium">Próxima Consulta</h3></div>
          {na ? (
            <div className="space-y-1">
              <div className="font-heading text-2xl font-semibold">{new Date(na.date + "T00:00").toLocaleDateString("pt-BR")}</div>
              <div className="text-muted-foreground">{na.time} · {na.type}</div>
              <StatusBadge status={na.status} />
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">Nenhuma consulta agendada.</p>
              <Button size="sm" className="rounded-full mt-3" onClick={() => nav("/app/consultas")} data-testid="schedule-btn">Agendar consulta</Button>
            </div>
          )}
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <QuickCard icon={Route} label="Minha Jornada" onClick={() => nav("/app/jornada")} testid="q-journey" />
        <QuickCard icon={Dumbbell} label="Meus Exercícios" onClick={() => nav("/app/exercicios")} testid="q-exercises" />
        <QuickCard icon={Library} label="Conteúdos" onClick={() => nav("/app/conteudos")} testid="q-content" />
        <QuickCard icon={CalendarDays} label="Agendar consulta" onClick={() => nav("/app/consultas")} testid="q-schedule" />
      </div>

      {recommended.length > 0 && (
        <Card className="p-6 mt-6">
          <h3 className="font-heading font-medium mb-1">Conteúdos recomendados</h3>
          <p className="text-sm text-muted-foreground mb-4">Conteúdo recomendado pelo seu hipnoterapeuta.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {recommended.map((c) => (
              <div key={c.id} className="rounded-xl border border-border p-4 hover:border-primary/40 transition-colors cursor-pointer" onClick={() => nav("/app/conteudos")}>
                <div className="text-xs text-primary font-medium mb-1">{c.category}</div>
                <div className="font-medium text-sm">{c.title}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Disclaimer />
    </div>
  );
}

function QuickCard({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="rounded-2xl border border-border bg-card p-5 text-left hover:-translate-y-1 hover:shadow-lg hover:border-primary/30 transition-all duration-200">
      <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
      <div className="font-medium">{label}</div>
    </button>
  );
}
